import type { AdminPermission, CurrentActor } from "../api/session-api";
import { adminSurfaceRoutes } from "../app/admin-routes";
import { canReadScheduleCalendar } from "./calendar-access";

export const adminSurfaces = adminSurfaceRoutes
  .filter(({ handle }) => handle.navigation !== false)
  .map(({ path, handle }) => ({
    path,
    label: handle.label,
    permission: handle.permission,
    ...(handle.alternativePermissions
      ? { alternativePermissions: handle.alternativePermissions }
      : {}),
    icon: handle.icon,
  }));

export function hasPermission(actor: CurrentActor | null, permission: AdminPermission): boolean {
  // These retained modules are outside the MVP; minimum partner/activity editors remain enabled.
  if (
    permission === "partner.import" ||
    permission.startsWith("approvals.") ||
    permission.startsWith("partner-contacts.") ||
    permission.startsWith("partner-team.") ||
    permission.startsWith("roles.")
  )
    return false;
  return actor?.permissions.includes(permission) ?? false;
}

/** New shared records have no owning Partner and require platform catalogue scope. */
export function canCreateSharedReference(
  actor: CurrentActor | null,
  permission: "destinations.manage" | "experiences.manage",
) {
  return actor?.catalogueScope?.kind === "PLATFORM_ALL" && hasPermission(actor, permission);
}

export function defaultAdminPath(actor: CurrentActor | null): string {
  const surfaces = visibleAdminSurfaces(actor);
  return surfaces.find(({ path }) => path === "/dashboard")?.path ?? surfaces[0]?.path ?? "/login";
}

export function hasSurfacePermission(
  actor: CurrentActor | null,
  surface: { permission: AdminPermission; alternativePermissions?: readonly AdminPermission[] },
) {
  if (surface.permission === "partner-schedules.read") return canReadScheduleCalendar(actor);
  return (
    hasPermission(actor, surface.permission) ||
    (surface.alternativePermissions?.some((permission) => hasPermission(actor, permission)) ??
      false)
  );
}

export function visibleAdminSurfaces(actor: CurrentActor | null) {
  return adminSurfaces.filter((surface) => hasSurfacePermission(actor, surface));
}
